import { useEffect, useMemo, useState } from 'react';
import { Plus, MapPin } from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney, getRegion } from '@nako/core';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { iconsForIndustry } from '@/lib/industry-icons';
import {
  fetchAccountMap,
  localYmd,
  postJournalEntry,
  r2,
  type PostLine,
} from '@/lib/journal-postings';

interface FixedAsset {
  id: string;
  business_id: string;
  name: string;
  category: string | null;
  cost: number;
  currency: 'BWP' | 'ZAR' | 'USD';
  acquired_date: string;
  location: string | null;
  status: 'in_service' | 'retired' | 'disposed';
  depreciation_method: string | null;
  useful_life_years: number | null;
  accumulated_depreciation: number;
  notes: string | null;
}

// Where the money to buy the asset came from — the credit side of the
// capitalisation entry. All four codes are standard seeded accounts.
const FUNDING_OPTIONS: Array<{ code: string; label: string }> = [
  { code: '1000', label: 'Cash / Bank' },
  { code: '2000', label: 'On credit (supplier)' },
  { code: '2300', label: 'Loan / financing' },
  { code: '3000', label: 'Owner contribution' },
];
const fundingLabel = (code: string) =>
  FUNDING_OPTIONS.find((o) => o.code === code)?.label ?? 'Funding';

export default function FixedAssets() {
  const business = useAuthStore((s) => s.business)!;
  const session = useAuthStore((s) => s.session);
  const regionCode = useRegionStore((s) => s.code);
  const toast = useToast();
  const Icon = iconsForIndustry(business.industry).assets;
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [showDisposed, setShowDisposed] = useState(false);
  const [open, setOpen] = useState(false);
  const [depBusy, setDepBusy] = useState(false);
  const [disposing, setDisposing] = useState<FixedAsset | null>(null);
  const [disposeBusy, setDisposeBusy] = useState(false);
  const [disposalForm, setDisposalForm] = useState({
    date: '',
    proceeds: 0,
    proceeds_account: '1000',
  });
  const [form, setForm] = useState({
    name: '',
    category: '',
    cost: 0,
    currency: (getRegion(regionCode).currencyCode as FixedAsset['currency']) || 'BWP',
    acquired_date: '',
    location: '',
    useful_life: 10,
    funding: '1000',
  });

  useEffect(() => {
    void load();
  }, [business.id]);

  async function load() {
    const { data } = await supabase
      .from('fixed_assets')
      .select('*')
      .eq('business_id', business.id)
      .order('acquired_date', { ascending: false });
    setAssets((data ?? []) as FixedAsset[]);
  }

  async function create() {
    if (!form.name || !form.cost) {
      toast.show('Name and cost are required', { tone: 'warning' });
      return;
    }
    const cost = Number(form.cost);
    const acquiredDate = form.acquired_date || localYmd();
    const { data: inserted } = await supabase
      .from('fixed_assets')
      .insert({
        business_id: business.id,
        name: form.name,
        category: form.category || null,
        cost,
        currency: form.currency,
        acquired_date: acquiredDate,
        location: form.location || null,
        status: 'in_service',
        depreciation_method: 'straight_line',
        useful_life_years: Number(form.useful_life) || 10,
        accumulated_depreciation: 0,
        notes: null,
      })
      .select()
      .single();

    // Capitalise to the ledger: Dr Fixed Assets at Cost (1500) / Cr funding
    // source. Home-currency assets only — foreign-currency assets are tracked
    // in the register but never FX-converted into the GL (matches the
    // depreciation run + register totals).
    const assetId = (inserted as { id: string } | null)?.id ?? null;
    let posted = false;
    if (assetId && form.currency === homeCurrency && cost > 0) {
      const accts = await fetchAccountMap(business.id);
      const fa = accts.get('1500');
      const fund = accts.get(form.funding);
      if (fa && fund) {
        posted = await postJournalEntry({
          businessId: business.id,
          date: acquiredDate,
          reference: `Asset acquisition — ${form.name}`,
          description: `Capitalised ${form.name}`,
          source: 'purchase',
          sourceId: assetId,
          createdBy: session?.user.id ?? null,
          lines: [
            { account_id: fa, debit: cost, credit: 0, description: `${form.name} (cost)` },
            { account_id: fund, debit: 0, credit: cost, description: fundingLabel(form.funding) },
          ],
        });
      }
    }
    toast.show(posted ? 'Asset added and capitalised to the ledger.' : 'Asset added.', {
      tone: 'success',
    });
    setOpen(false);
    setForm({
      name: '',
      category: '',
      cost: 0,
      currency: (getRegion(regionCode).currencyCode as FixedAsset['currency']) || 'BWP',
      acquired_date: '',
      location: '',
      useful_life: 10,
      funding: '1000',
    });
    void load();
  }

  // Dispose of an asset and post the derecognition entry:
  //   Dr Cash/Bank (proceeds)         — if any proceeds
  //   Dr Accumulated Depreciation     — reverse depreciation booked to date
  //   Cr Fixed Assets at Cost         — remove the asset at cost
  //   Dr/Cr Gain-or-loss on disposal  — the balancing P&L line (4200)
  // Self-balancing for any proceeds/NBV combination. Home-currency only;
  // foreign-currency assets are simply retired (never hit the GL). Idempotent.
  async function disposeAsset() {
    const a = disposing;
    if (!a) return;
    setDisposeBusy(true);
    try {
      const date = disposalForm.date || localYmd();
      const proceeds = r2(Math.max(0, Number(disposalForm.proceeds) || 0));
      const cost = r2(a.cost);
      const accumDep = r2(a.accumulated_depreciation || 0);
      const nbv = r2(cost - accumDep);
      const gainLoss = r2(proceeds - nbv); // > 0 gain, < 0 loss

      if (a.currency !== homeCurrency) {
        await supabase.from('fixed_assets').update({ status: 'disposed' }).eq('id', a.id);
        toast.show('Foreign-currency asset retired (no ledger posting).', { tone: 'success' });
        setDisposing(null);
        void load();
        return;
      }

      const accts = await fetchAccountMap(business.id);
      const fa = accts.get('1500');
      const accum = accts.get('1510');
      const cashAcc = accts.get(disposalForm.proceeds_account) ?? accts.get('1000');
      const disposalPl = accts.get('4200'); // Gain/(Loss) on Asset Disposal
      if (!fa || !accum) {
        toast.show('Missing Fixed Assets (1500) / Accumulated Depreciation (1510) accounts.', {
          tone: 'error',
        });
        return;
      }
      if (proceeds > 0 && !cashAcc) {
        toast.show('Missing Cash/Bank account for the proceeds.', { tone: 'error' });
        return;
      }
      if (gainLoss !== 0 && !disposalPl) {
        toast.show('Missing Gain/(Loss) on Asset Disposal (4200) account.', { tone: 'error' });
        return;
      }

      const lines: PostLine[] = [
        { account_id: fa, debit: 0, credit: cost, description: `${a.name} — derecognise cost` },
      ];
      if (accumDep > 0)
        lines.push({
          account_id: accum,
          debit: accumDep,
          credit: 0,
          description: `${a.name} — reverse accumulated depreciation`,
        });
      if (proceeds > 0 && cashAcc)
        lines.push({
          account_id: cashAcc,
          debit: proceeds,
          credit: 0,
          description: `${a.name} — disposal proceeds`,
        });
      if (gainLoss > 0 && disposalPl)
        lines.push({
          account_id: disposalPl,
          debit: 0,
          credit: gainLoss,
          description: `Gain on disposal of ${a.name}`,
        });
      if (gainLoss < 0 && disposalPl)
        lines.push({
          account_id: disposalPl,
          debit: -gainLoss,
          credit: 0,
          description: `Loss on disposal of ${a.name}`,
        });

      const posted = await postJournalEntry({
        businessId: business.id,
        date,
        reference: `Disposal — ${a.name}`,
        description: `Disposal of ${a.name}${
          proceeds > 0 ? ` for ${formatMoney(proceeds, regionCode)}` : ' (no proceeds)'
        }`,
        source: 'adjustment',
        sourceId: a.id,
        createdBy: session?.user.id ?? null,
        lines,
      });
      await supabase.from('fixed_assets').update({ status: 'disposed' }).eq('id', a.id);
      toast.show(
        posted
          ? `Asset disposed${
              gainLoss !== 0
                ? ` — ${gainLoss > 0 ? 'gain' : 'loss'} ${formatMoney(Math.abs(gainLoss), regionCode)}`
                : ''
            }.`
          : 'Asset already disposed.',
        { tone: 'success' },
      );
      setDisposing(null);
      void load();
    } finally {
      setDisposeBusy(false);
    }
  }

  // Post one month's straight-line depreciation for every in-service asset.
  // Dr Depreciation (5900) / Cr Accumulated Depreciation (1510). Idempotent:
  // one entry per asset per calendar month. Dated today so it lands inside the
  // dashboard's month window.
  async function runDepreciation() {
    setDepBusy(true);
    try {
      const { data: accts } = await supabase
        .from('accounts')
        .select('id, gl_code')
        .eq('business_id', business.id);
      const acc = (code: string) =>
        (accts ?? []).find((a) => (a as { gl_code: string }).gl_code === code) as
          | { id: string }
          | undefined;
      const depExp = acc('5900'); // Depreciation (expense)
      const accumDep = acc('1510'); // Accumulated Depreciation (contra-asset)
      if (!depExp || !accumDep) {
        toast.show('Missing Depreciation (5900) / Accumulated Depreciation (1510) accounts.', {
          tone: 'error',
        });
        return;
      }
      const dateStr = localYmd();
      const ym = dateStr.slice(0, 7);
      let posted = 0;
      let skipped = 0;
      for (const a of assets) {
        if (a.status !== 'in_service') continue;
        const dep = r2(monthlyDepreciation(a));
        if (dep <= 0) continue;
        const ref = `Depreciation ${ym} — ${a.name}`;
        const { data: existing } = await supabase
          .from('journal_entries')
          .select('id')
          .eq('business_id', business.id)
          .eq('source', 'adjustment')
          .eq('source_id', a.id)
          .eq('reference', ref)
          .maybeSingle();
        if (existing) {
          skipped++;
          continue;
        }
        const { data: entry } = await supabase
          .from('journal_entries')
          .insert({
            business_id: business.id,
            entry_date: dateStr,
            reference: ref,
            description: `Monthly depreciation — ${a.name}`,
            source: 'adjustment',
            source_id: a.id,
            is_posted: true,
            posted_at: new Date().toISOString(),
            created_by: session?.user.id ?? null,
          })
          .select()
          .single();
        if (!entry) continue;
        const eid = (entry as { id: string }).id;
        const { error: linesErr } = await supabase.from('journal_lines').insert([
          {
            entry_id: eid,
            account_id: depExp.id,
            debit: dep,
            credit: 0,
            vat_amount: 0,
            vat_rate: 0,
            description: `Depreciation — ${a.name}`,
          },
          {
            entry_id: eid,
            account_id: accumDep.id,
            debit: 0,
            credit: dep,
            vat_amount: 0,
            vat_rate: 0,
            description: `Accumulated depreciation — ${a.name}`,
          },
        ]);
        // Lines failed but the header committed (separate request) — delete
        // the orphan so it can't corrupt the trial balance, and do NOT bump
        // the asset's accumulated depreciation for an entry that never posted.
        if (linesErr) {
          await supabase.from('journal_entries').delete().eq('id', eid);
          continue;
        }
        await supabase
          .from('fixed_assets')
          .update({ accumulated_depreciation: r2((a.accumulated_depreciation || 0) + dep) })
          .eq('id', a.id);
        posted++;
      }
      toast.show(
        posted > 0
          ? `Depreciation posted for ${posted} asset${posted === 1 ? '' : 's'}${skipped ? ` (${skipped} already done this month)` : ''}.`
          : skipped > 0
            ? 'Depreciation already posted this month.'
            : 'No in-service assets to depreciate.',
        { tone: posted > 0 ? 'success' : 'warning' },
      );
      void load();
    } finally {
      setDepBusy(false);
    }
  }

  // Group by category for the register view
  const disposedCount = useMemo(
    () => assets.filter((a) => a.status === 'disposed').length,
    [assets],
  );

  const grouped = useMemo(() => {
    const m = new Map<string, FixedAsset[]>();
    for (const a of assets) {
      // Disposed assets are kept (the disposal journal entry references them) but
      // hidden from the active register by default — toggle to review them.
      if (!showDisposed && a.status === 'disposed') continue;
      const key = a.category ?? 'Uncategorised';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(a);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [assets, showDisposed]);

  const homeCurrency = getRegion(regionCode).currencyCode;
  const totalsBwp = useMemo(() => {
    // Sum home-currency cost only (don't FX-convert foreign currencies).
    let home = 0;
    let foreign = 0;
    for (const a of assets) {
      // Disposed assets are derecognised in the ledger (Cr 1500), so exclude them
      // here too — keeps the register's cost total tied to GL 1500.
      if (a.status === 'disposed') continue;
      if (a.currency === homeCurrency) home += a.cost;
      else foreign += a.cost;
    }
    return { bwp: home, nonBwp: foreign };
  }, [assets, homeCurrency]);

  /** Straight-line monthly depreciation per asset. Zero when life or method missing. */
  function monthlyDepreciation(a: FixedAsset): number {
    if (!a.useful_life_years || a.useful_life_years <= 0) return 0;
    if (a.depreciation_method && a.depreciation_method !== 'straight_line') return 0;
    return Math.max(0, a.cost / (a.useful_life_years * 12));
  }

  /** Carrying value = cost - accumulated_depreciation, floored at zero. */
  function carryingValue(a: FixedAsset): number {
    return Math.max(0, a.cost - (a.accumulated_depreciation || 0));
  }

  const totalMonthlyDep = assets
    .filter((a) => a.status === 'in_service' && a.currency === homeCurrency)
    .reduce((s, a) => s + monthlyDepreciation(a), 0);

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display flex items-center gap-2 text-2xl">
          <Icon size={22} /> Fixed Asset Register
        </h1>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void runDepreciation()}
            loading={depBusy}
            disabled={assets.length === 0}
          >
            Run depreciation
          </Button>
          <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setOpen(true)}>
            Add asset
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <Card compact>
          <p className="text-muted text-xs">Cost ({homeCurrency})</p>
          <p className="tnum font-mono text-xl">{formatMoney(totalsBwp.bwp, regionCode)}</p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">Cost (foreign)</p>
          <p className="tnum font-mono text-xl">
            {totalsBwp.nonBwp > 0 ? `~${formatMoney(totalsBwp.nonBwp, regionCode)}` : '—'}
          </p>
          <p className="text-muted text-[10px]">
            Foreign-currency assets shown at carrying cost, not FX-converted
          </p>
        </Card>
        <Card compact>
          <p className="text-muted text-xs">Monthly depreciation</p>
          <p className="tnum font-mono text-xl">{formatMoney(totalMonthlyDep, regionCode)}</p>
          <p className="text-muted text-[10px]">Straight-line, in-service assets only</p>
        </Card>
      </div>

      {disposedCount > 0 && (
        <div className="mb-3 flex justify-end">
          <button
            onClick={() => setShowDisposed((v) => !v)}
            className="border-line text-muted hover:text-ink rounded-pill border bg-white px-3 py-1.5 text-xs font-medium"
          >
            {showDisposed ? `Hide disposed (${disposedCount})` : `Show disposed (${disposedCount})`}
          </button>
        </div>
      )}

      {assets.length === 0 ? (
        <Card className="py-10 text-center">
          <Icon size={32} className="text-muted mx-auto mb-3 opacity-50" />
          <p className="text-muted mb-4">No fixed assets registered yet.</p>
          <Button onClick={() => setOpen(true)} leftIcon={<Plus size={16} />}>
            Register your first asset
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(([category, list]) => (
            <div key={category}>
              <h2 className="text-muted mb-2 text-xs font-semibold uppercase tracking-wider">
                {category}
              </h2>
              <div className="space-y-2">
                {list.map((a) => (
                  <Card key={a.id} compact>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{a.name}</p>
                        <p className="text-muted text-xs">
                          Acquired {a.acquired_date}
                          {a.useful_life_years ? ` · ${a.useful_life_years}y life` : ''}
                          {a.location && (
                            <>
                              {' · '}
                              <MapPin size={10} className="inline" /> {a.location}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="tnum font-mono text-sm font-semibold">
                          {a.currency === homeCurrency
                            ? formatMoney(a.cost, regionCode)
                            : `${a.currency} ${a.cost.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`}
                        </p>
                        {a.currency === homeCurrency && (a.accumulated_depreciation || 0) > 0 && (
                          <p className="tnum text-muted font-mono text-[10px]">
                            NBV {formatMoney(carryingValue(a), regionCode)}
                          </p>
                        )}
                        {a.currency === homeCurrency &&
                          a.status === 'in_service' &&
                          monthlyDepreciation(a) > 0 && (
                            <p className="text-muted font-mono text-[10px]">
                              {formatMoney(monthlyDepreciation(a), regionCode)} / mo
                            </p>
                          )}
                        <Pill
                          tone={
                            a.status === 'in_service'
                              ? 'green'
                              : a.status === 'retired'
                                ? 'neutral'
                                : 'red'
                          }
                          className="mt-1"
                        >
                          {a.status.replace('_', ' ')}
                        </Pill>
                        {a.status === 'in_service' && (
                          <button
                            onClick={() => {
                              setDisposalForm({
                                date: localYmd(),
                                proceeds: 0,
                                proceeds_account: '1000',
                              });
                              setDisposing(a);
                            }}
                            className="text-muted hover:text-red mt-1 block w-full text-right text-[11px] underline"
                          >
                            Dispose
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Register fixed asset">
        <div className="space-y-3">
          <Input
            label="Asset name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Input
            label="Category"
            placeholder="Vehicles, Kitchen equipment, IT, Buildings…"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Input
                label="Cost"
                type="number"
                numeric
                value={form.cost || ''}
                onChange={(e) => setForm({ ...form, cost: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Currency</label>
              <select
                value={form.currency}
                onChange={(e) =>
                  setForm({ ...form, currency: e.target.value as FixedAsset['currency'] })
                }
                className="min-h-tap rounded-btn border-line border bg-white px-3"
              >
                <option value="BWP">BWP</option>
                <option value="ZAR">ZAR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
          <Input
            label="Acquired date"
            type="date"
            value={form.acquired_date}
            onChange={(e) => setForm({ ...form, acquired_date: e.target.value })}
          />
          <Input
            label="Location (optional)"
            placeholder="Branch, room, vehicle reg…"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
          <Input
            label="Useful life (years)"
            type="number"
            numeric
            value={form.useful_life || ''}
            onChange={(e) => setForm({ ...form, useful_life: Number(e.target.value) || 0 })}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Paid from</label>
            <select
              value={form.funding}
              onChange={(e) => setForm({ ...form, funding: e.target.value })}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              {FUNDING_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-muted text-[10px]">
              Capitalises to Fixed Assets (1500) against this account. Home-currency assets only.
            </p>
          </div>
          <Button full onClick={create}>
            Register asset
          </Button>
        </div>
      </Modal>

      {/* Dispose asset */}
      <Modal
        open={disposing !== null}
        onClose={() => setDisposing(null)}
        title={disposing ? `Dispose ${disposing.name}` : ''}
      >
        {disposing &&
          (() => {
            const cost = r2(disposing.cost);
            const accumDep = r2(disposing.accumulated_depreciation || 0);
            const nbv = r2(cost - accumDep);
            const proceeds = r2(Math.max(0, Number(disposalForm.proceeds) || 0));
            const gainLoss = r2(proceeds - nbv);
            const isForeign = disposing.currency !== homeCurrency;
            return (
              <div className="space-y-3">
                <div className="bg-paper-2 rounded-btn space-y-1 p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted">Cost</span>
                    <span className="tnum font-mono">{formatMoney(cost, regionCode)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">Accumulated depreciation</span>
                    <span className="tnum font-mono">{formatMoney(accumDep, regionCode)}</span>
                  </div>
                  <div className="border-line flex justify-between border-t pt-1 font-medium">
                    <span>Net book value</span>
                    <span className="tnum font-mono">{formatMoney(nbv, regionCode)}</span>
                  </div>
                </div>

                {isForeign ? (
                  <p className="text-muted text-xs">
                    This is a {disposing.currency} asset — it is tracked in the register but not in
                    the home-currency ledger, so disposal just retires it (no journal entry).
                  </p>
                ) : (
                  <>
                    <Input
                      label="Disposal date"
                      type="date"
                      value={disposalForm.date}
                      onChange={(e) => setDisposalForm({ ...disposalForm, date: e.target.value })}
                    />
                    <Input
                      label="Sale proceeds"
                      type="number"
                      numeric
                      leftAddon="P"
                      hint="0 if scrapped / written off"
                      value={disposalForm.proceeds || ''}
                      onChange={(e) =>
                        setDisposalForm({ ...disposalForm, proceeds: Number(e.target.value) || 0 })
                      }
                    />
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium">Proceeds received into</label>
                      <select
                        value={disposalForm.proceeds_account}
                        onChange={(e) =>
                          setDisposalForm({ ...disposalForm, proceeds_account: e.target.value })
                        }
                        className="min-h-tap rounded-btn border-line border bg-white px-3"
                      >
                        <option value="1000">Cash / Bank</option>
                        <option value="1100">Receivable (sold on credit)</option>
                      </select>
                    </div>
                    {gainLoss !== 0 && (
                      <div className="rounded-btn flex justify-between px-1 text-sm font-medium">
                        <span>{gainLoss > 0 ? 'Gain on disposal' : 'Loss on disposal'}</span>
                        <span
                          className={`tnum font-mono ${gainLoss > 0 ? 'text-green' : 'text-red'}`}
                        >
                          {formatMoney(Math.abs(gainLoss), regionCode)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Button variant="secondary" onClick={() => setDisposing(null)}>
                    Cancel
                  </Button>
                  <Button onClick={() => void disposeAsset()} loading={disposeBusy}>
                    {isForeign ? 'Retire asset' : 'Dispose & post'}
                  </Button>
                </div>
              </div>
            );
          })()}
      </Modal>
    </main>
  );
}
